import { useQuery } from '@tanstack/react-query';
import { api, type SessionRow, type EhrRow } from '../lib/api.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { AttentionTray } from '../components/dashboard/AttentionTray.js';
import { HeroStats } from '../components/dashboard/HeroStats.js';
import { SessionsChart } from '../components/dashboard/SessionsChart.js';
import { ConflictsChart } from '../components/dashboard/ConflictsChart.js';
import { HeartbeatStrip } from '../components/dashboard/HeartbeatStrip.js';
import { RunningSessionCards } from '../components/dashboard/RunningSessionCards.js';
import { RecentSessionsTable } from '../components/dashboard/RecentSessionsTable.js';
import { LiveFeed } from '../components/dashboard/LiveFeed.js';
import { AiUsageWidget } from '../components/dashboard/AiUsageWidget.js';

export function DashboardPage() {
  usePageTitle('Dashboard');

  const { data: sessionsData } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.listSessions(),
    refetchInterval: 15_000,
  });

  const { data: ehrsData } = useQuery({
    queryKey: ['ehrs'],
    queryFn: () => api.listEhrs(),
    refetchInterval: 60_000,
  });

  const { data: conflictCountData } = useQuery({
    queryKey: ['conflicts/count'],
    queryFn: () => api.getConflictCount(),
    refetchInterval: 30_000,
  });

  const { data: conflictsData } = useQuery({
    queryKey: ['conflicts'],
    queryFn: () => api.listConflicts({ status: 'open' }),
    refetchInterval: 60_000,
  });

  const sessions: SessionRow[] = sessionsData?.sessions ?? [];
  const ehrs: EhrRow[] = ehrsData?.ehrs ?? [];
  const conflictCount = conflictCountData?.count ?? 0;

  const oldestConflictAt = conflictsData?.conflicts.length
    ? conflictsData.conflicts.reduce((oldest, c) =>
        c.createdAt < oldest ? c.createdAt : oldest,
        conflictsData.conflicts[0]?.createdAt ?? '',
      )
    : null;

  return (
    <div className="flex flex-col h-full">
      <AttentionTray
        sessions={sessions}
        conflictCount={conflictCount}
        oldestConflictAt={oldestConflictAt}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <HeroStats />

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-7">
            <SessionsChart />
          </div>
          <div className="col-span-5">
            <ConflictsChart />
          </div>
        </div>

        <div
          className="glass rounded-xl px-4 py-2"
          style={{ border: '1px solid var(--glass-border)' }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary mb-2">
            Session heartbeat
          </div>
          <HeartbeatStrip sessions={sessions} />
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4">
            <div
              className="glass rounded-2xl p-5"
              style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
            >
              <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary mb-4">
                Running sessions
              </div>
              <RunningSessionCards sessions={sessions} ehrs={ehrs} />
            </div>

            <div
              className="glass rounded-2xl p-5"
              style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
            >
              <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary mb-4">
                Recent sessions
              </div>
              <RecentSessionsTable sessions={sessions} ehrs={ehrs} />
            </div>
          </div>

          <div className="col-span-4 flex flex-col gap-4">
            <AiUsageWidget />
            <LiveFeed />
          </div>
        </div>
      </div>
    </div>
  );
}
